'use client';

import { createContext, useContext, useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useAuth } from './auth-context';
import { fetchWithAuth } from '@/lib/auth/fetch-with-auth';
import type { RoleType, OrganizationPermissionName } from '@/lib/db/types';

interface UserOrganization {
  organizationId: string;
  organizationName: string;
  role: string;
  isOwner: boolean;
  permissions: OrganizationPermissionName[];
}

interface SessionState {
  roles: RoleType[];
  entitlements: string[];
  isAdmin: boolean;
  isSuperAdmin: boolean;
  platformPermissions: string[];
  organizations: UserOrganization[];
  activeOrganization: UserOrganization | null;
  isParticipantOnly: boolean;
}

export interface UserSessionData extends SessionState {
  loading: boolean;
  error: string | null;
}

interface UserSessionActions {
  refresh: () => void;
  setActiveOrganization: (organizationId: string) => Promise<void>;
}

const defaultSessionState: SessionState = {
  roles: [],
  entitlements: [],
  isAdmin: false,
  isSuperAdmin: false,
  platformPermissions: [],
  organizations: [],
  activeOrganization: null,
  isParticipantOnly: false,
};

const defaultSessionData: UserSessionData = {
  ...defaultSessionState,
  loading: true,
  error: null,
};

const RETRY_DELAY_MS = 1000;
const MAX_RETRIES = 3;
const ACTIVE_ORG_STORAGE_KEY = 'platform:active_org';

const UserSessionDataContext = createContext<UserSessionData>(defaultSessionData);

const UserSessionActionsContext = createContext<UserSessionActions>({
  refresh: () => {},
  setActiveOrganization: async () => {},
});

export function UserSessionProvider({ children }: { children: React.ReactNode }) {
  const { user, session: authSession, loading: authLoading } = useAuth();
  const [sessionState, setSessionState] = useState<SessionState>(defaultSessionState);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const lastTokenRef = useRef<string | null>(null);
  const sessionRef = useRef<SessionState>(defaultSessionState);
  const overrideOrgIdRef = useRef<string | null>(null);
  const fetchCounterRef = useRef(0);

  const accessToken = authSession?.access_token ?? null;
  const userId = user?.id ?? null;

  useEffect(() => {
    if (authLoading) return;

    if (!userId || !accessToken) {
      lastTokenRef.current = null;
      overrideOrgIdRef.current = null;
      sessionRef.current = defaultSessionState;
      setSessionState(defaultSessionState);
      setLoading(false);
      return;
    }

    if (accessToken === lastTokenRef.current) {
      return;
    }

    fetchCounterRef.current += 1;
    const thisFetch = fetchCounterRef.current;

    setLoading(true);
    setError(null);

    const doFetch = async () => {
      let attempts = 0;
      let lastErrorMessage: string | null = null;
      while (attempts <= MAX_RETRIES) {
        if (fetchCounterRef.current !== thisFetch) return;

        try {
          const res = await fetchWithAuth('/api/user/session-context', authSession);

          if (fetchCounterRef.current !== thisFetch) return;

          if (res.ok) {
            const data = await res.json();
            if (data) {
              lastTokenRef.current = accessToken;
              const orgs: UserOrganization[] = Array.isArray(data.organizations) ? data.organizations : [];
              let isParticipantOnly = false;
              if (orgs.length === 0 && !data.isSuperAdmin && !data.isAdmin) {
                try {
                  const statusRes = await fetchWithAuth('/api/participant/status', authSession);
                  if (statusRes.ok) {
                    const statusData = await statusRes.json();
                    isParticipantOnly = statusData.is_participant_only === true;
                  }
                } catch {}
              }

              if (fetchCounterRef.current !== thisFetch) return;

              let newSession: SessionState = {
                roles: Array.isArray(data.roles) ? data.roles : [],
                entitlements: Array.isArray(data.entitlements) ? data.entitlements : [],
                isSuperAdmin: data.isSuperAdmin ?? false,
                isAdmin: data.isAdmin ?? false,
                platformPermissions: Array.isArray(data.platformPermissions) ? data.platformPermissions : [],
                organizations: orgs,
                activeOrganization: data.activeOrganization ?? null,
                isParticipantOnly,
              };

              const storedOrgId = typeof window !== 'undefined'
                ? localStorage.getItem(`${ACTIVE_ORG_STORAGE_KEY}:${userId}`)
                : null;
              const overrideId = storedOrgId || overrideOrgIdRef.current;

              if (overrideId) {
                const overrideOrg = newSession.organizations.find(
                  (o) => o.organizationId === overrideId
                );
                if (overrideOrg) {
                  newSession = { ...newSession, activeOrganization: overrideOrg };
                  overrideOrgIdRef.current = overrideId;
                } else {
                  overrideOrgIdRef.current = null;
                  if (typeof window !== 'undefined') {
                    localStorage.removeItem(`${ACTIVE_ORG_STORAGE_KEY}:${userId}`);
                  }
                }
              }

              sessionRef.current = newSession;
              setSessionState(newSession);
              setError(null);
              setLoading(false);
              return;
            }
          } else if (res.status === 401) {
            sessionRef.current = defaultSessionState;
            setSessionState(defaultSessionState);
            setError('Authentication expired. Please sign in again.');
            setLoading(false);
            return;
          } else {
            lastErrorMessage = `Session request failed (HTTP ${res.status})`;
          }
        } catch (err) {
          console.error('[UserSession] Fetch attempt failed:', err);
          lastErrorMessage = err instanceof Error ? err.message : 'Network error loading session';
        }

        attempts++;
        if (attempts <= MAX_RETRIES) {
          await new Promise((r) => setTimeout(r, RETRY_DELAY_MS));
        }
      }

      setError(lastErrorMessage ?? 'Failed to load session context after multiple attempts');
      setLoading(false);
    };

    doFetch();
  }, [authLoading, userId, accessToken]);

  const refresh = useCallback(() => {
    lastTokenRef.current = null;
    fetchCounterRef.current += 1;
    const thisFetch = fetchCounterRef.current;

    if (!userId) return;

    setLoading(true);
    setError(null);

    const doFetch = async () => {
      try {
        const res = await fetchWithAuth('/api/user/session-context', authSession);

        if (fetchCounterRef.current !== thisFetch) return;

        if (res.ok) {
          const data = await res.json();
          if (data) {
            const orgs: UserOrganization[] = Array.isArray(data.organizations) ? data.organizations : [];
            let isParticipantOnly = false;
            if (orgs.length === 0 && !data.isSuperAdmin && !data.isAdmin) {
              try {
                const statusRes = await fetchWithAuth('/api/participant/status', authSession);
                if (statusRes.ok) {
                  const statusData = await statusRes.json();
                  isParticipantOnly = statusData.is_participant_only === true;
                }
              } catch {}
            }

            if (fetchCounterRef.current !== thisFetch) return;

            let newSession: SessionState = {
              roles: Array.isArray(data.roles) ? data.roles : [],
              entitlements: Array.isArray(data.entitlements) ? data.entitlements : [],
              isSuperAdmin: data.isSuperAdmin ?? false,
              isAdmin: data.isAdmin ?? false,
              platformPermissions: Array.isArray(data.platformPermissions) ? data.platformPermissions : [],
              organizations: orgs,
              activeOrganization: data.activeOrganization ?? null,
              isParticipantOnly,
            };

            const storedOrgId = typeof window !== 'undefined'
              ? localStorage.getItem(`${ACTIVE_ORG_STORAGE_KEY}:${userId}`)
              : null;
            const overrideId = storedOrgId || overrideOrgIdRef.current;

            if (overrideId) {
              const overrideOrg = newSession.organizations.find(
                (o) => o.organizationId === overrideId
              );
              if (overrideOrg) {
                newSession = { ...newSession, activeOrganization: overrideOrg };
              }
            }

            sessionRef.current = newSession;
            setSessionState(newSession);
            setError(null);
            lastTokenRef.current = accessToken;
          }
        } else {
          setError(`Session refresh failed (HTTP ${res.status})`);
        }
      } catch (err) {
        console.error('[UserSession] Refresh failed:', err);
        setError(err instanceof Error ? err.message : 'Failed to refresh session');
      } finally {
        if (fetchCounterRef.current === thisFetch) {
          setLoading(false);
        }
      }
    };

    doFetch();
  }, [userId, accessToken]);

  const setActiveOrganization = useCallback(async (organizationId: string) => {
    const targetOrg = sessionRef.current.organizations.find((o) => o.organizationId === organizationId);
    if (!targetOrg) return;

    if (userId && typeof window !== 'undefined') {
      localStorage.setItem(`${ACTIVE_ORG_STORAGE_KEY}:${userId}`, organizationId);
    }
    overrideOrgIdRef.current = organizationId;

    const optimistic = { ...sessionRef.current, activeOrganization: targetOrg };
    sessionRef.current = optimistic;
    setSessionState(optimistic);

    fetchWithAuth('/api/user/preferences', authSession, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ active_organization_id: organizationId }),
    }).catch((err) => {
      console.error('[UserSession] Background sync of active org failed:', err);
    });
  }, [userId]);

  const combinedLoading = loading || authLoading;

  const dataValue = useMemo(
    () => ({ ...sessionState, loading: combinedLoading, error }),
    [sessionState, combinedLoading, error]
  );

  const actionsValue = useMemo(
    () => ({ refresh, setActiveOrganization }),
    [refresh, setActiveOrganization]
  );

  return (
    <UserSessionActionsContext.Provider value={actionsValue}>
      <UserSessionDataContext.Provider value={dataValue}>
        {children}
      </UserSessionDataContext.Provider>
    </UserSessionActionsContext.Provider>
  );
}

export function useUserSession() {
  const data = useContext(UserSessionDataContext);
  const actions = useContext(UserSessionActionsContext);
  return useMemo(() => ({ ...data, ...actions }), [data, actions]);
}

export function useUserSessionData() {
  return useContext(UserSessionDataContext);
}

export function useUserSessionActions() {
  return useContext(UserSessionActionsContext);
}
